import { Link } from 'react-router-dom';
import { BlogArticle } from '../../../mocks/blog';

interface FeaturedArticleProps {
  article: BlogArticle;
}

export default function FeaturedArticle({ article }: FeaturedArticleProps) {
  return (
    <Link to={`/blog/${article.slug}`} className="group relative bg-cream-50 border border-gold-200/60 rounded-3xl overflow-hidden hover:border-gold-400 transition-all duration-500 cursor-pointer block">
      {/* Golden glow effect on hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-gold-500/0 via-gold-500/5 to-gold-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-10"></div>
      
      <div className="grid md:grid-cols-2 gap-0">
        {/* Image */}
        <div className="relative h-80 md:h-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal-950/60 via-charcoal-950/20 to-transparent z-10"></div>
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
          />
          
          {/* Featured badge */}
          <div className="absolute top-6 left-6 z-20">
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-gold-500 to-gold-600 text-white rounded-full">
              <i className="ri-star-fill"></i>
              <span className="text-sm font-semibold whitespace-nowrap font-['Montserrat']">Artigo em Destaque</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-10 flex flex-col justify-center">
          {/* Category */}
          <div className="mb-4">
            <span className={`inline-block px-4 py-1.5 bg-gradient-to-r ${article.categoryColor} text-white text-sm font-semibold rounded-full font-['Montserrat']`}>
              {article.category}
            </span>
          </div>

          {/* Title */}
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-charcoal-800 mb-4 group-hover:text-gold-600 transition-colors">
            {article.title}
          </h2>

          {/* Excerpt */}
          <p className="text-base text-charcoal-500 mb-6 leading-relaxed font-['Montserrat'] font-light">
            {article.excerpt}
          </p>

          {/* Meta info */}
          <div className="flex items-center gap-6 mb-6">
            <div className="flex items-center gap-3">
              <img
                src={article.author.avatar}
                alt={article.author.name}
                className="w-12 h-12 rounded-full object-cover border-2 border-gold-400"
              />
              <div>
                <p className="font-semibold text-charcoal-700 font-['Montserrat']">{article.author.name}</p>
                <p className="text-sm text-charcoal-400 font-['Montserrat']">
                  {new Date(article.publishedAt).toLocaleDateString('pt-BR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-charcoal-400">
              <i className="ri-time-line"></i>
              <span className="text-sm whitespace-nowrap font-['Montserrat']">{article.readTime} de leitura</span>
            </div>
          </div>

          {/* CTA */}
          <div>
            <span className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-700 text-white font-['Montserrat'] font-semibold rounded-xl group-hover:from-gold-600 group-hover:to-gold-800 transition-all hover:scale-105 whitespace-nowrap">
              Ler artigo completo
              <i className="ri-arrow-right-line"></i>
            </span>
          </div>
        </div>
      </div>

      {/* Bottom golden line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    </Link>
  );
}
